import { types, Instance } from 'mobx-state-tree';

// Viewport model to store x, y, zoom for a specific mindmap
const Viewport = types.model('Viewport', {
  mindmapId: types.identifier,
  x: types.number,
  y: types.number,
  zoom: types.number,
  updatedAt: types.optional(types.number, () => Date.now()),
});

// Default viewport settings
const DEFAULT_VIEWPORT = { x: 0, y: 0, zoom: 1 };

// MindmapViewportStore to manage viewport state per mindmap
export const MindmapViewportStore = types
  .model('MindmapViewportStore', {
    viewports: types.map(Viewport), // Map of mindmapId -> Viewport
  })
  .actions(self => ({
    setViewport(mindmapId: string, x: number, y: number, zoom: number) {
      // Validate inputs
      if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(zoom)) {
        console.warn('[MindmapViewportStore] Invalid viewport values:', { x, y, zoom });
        return;
      }
      
      if (zoom < 0.05 || zoom > 5.0) {
        console.warn('[MindmapViewportStore] Zoom out of range:', zoom);
        return;
      }
      
      self.viewports.set(mindmapId, {
        mindmapId,
        x,
        y,
        zoom,
        updatedAt: Date.now(),
      });
      
      console.log(`[MindmapViewportStore] Viewport saved for ${mindmapId}: x=${x.toFixed(0)}, y=${y.toFixed(0)}, zoom=${zoom.toFixed(2)}`);
      
      // Save to localStorage
      this.saveToLocalStorage();
    },
    
    getViewport(mindmapId: string) {
      const viewport = self.viewports.get(mindmapId);
      if (viewport) {
        return { x: viewport.x, y: viewport.y, zoom: viewport.zoom };
      }
      return DEFAULT_VIEWPORT;
    },
    
    clearViewport(mindmapId: string) {
      self.viewports.delete(mindmapId);
      console.log(`[MindmapViewportStore] Viewport cleared for ${mindmapId}`);
      this.saveToLocalStorage();
    },
    
    clearAll() {
      self.viewports.clear();
      console.log('[MindmapViewportStore] All viewports cleared');
      this.saveToLocalStorage();
    },
    
    // Save to localStorage
    saveToLocalStorage() {
      if (typeof window !== 'undefined') {
        try {
          const data: Record<string, any> = {};
          self.viewports.forEach((viewport, mindmapId) => {
            data[mindmapId] = {
              x: viewport.x,
              y: viewport.y,
              zoom: viewport.zoom,
              updatedAt: viewport.updatedAt,
            };
          });
          localStorage.setItem('mindmap-viewports', JSON.stringify(data));
          console.log(`[MindmapViewportStore] Saved ${self.viewports.size} viewports to localStorage`);
        } catch (error) {
          console.error('[MindmapViewportStore] Failed to save to localStorage:', error);
        }
      }
    },
    
    // Load from localStorage
    loadFromLocalStorage() {
      if (typeof window !== 'undefined') {
        try {
          const stored = localStorage.getItem('mindmap-viewports');
          if (stored) {
            const data = JSON.parse(stored);
            Object.entries(data).forEach(([mindmapId, viewport]: [string, any]) => {
              if (viewport && typeof viewport.x === 'number' && typeof viewport.y === 'number' && typeof viewport.zoom === 'number') {
                self.viewports.set(mindmapId, {
                  mindmapId,
                  x: viewport.x,
                  y: viewport.y,
                  zoom: viewport.zoom,
                  updatedAt: viewport.updatedAt || Date.now(),
                });
              }
            });
            console.log(`[MindmapViewportStore] Loaded ${self.viewports.size} viewports from localStorage`);
            return true;
          }
        } catch (error) {
          console.error('[MindmapViewportStore] Failed to load from localStorage:', error);
        }
      }
      return false;
    }
  }))
  .views(self => ({
    hasViewport(mindmapId: string) {
      return self.viewports.has(mindmapId);
    },
    
    get viewportCount() {
      return self.viewports.size;
    }
  }))
  .actions(self => ({
    afterCreate() {
      // Load viewports from localStorage on initialization
      self.loadFromLocalStorage();
    }
  }));

// Create types for the store
export interface IMindmapViewportStore extends Instance<typeof MindmapViewportStore> {}

// REMOVED: No longer creating a singleton instance here. AppStore is responsible.
// export const mindmapViewportStore = MindmapViewportStore.create();

// DISABLED persistence to prevent MST reference conflicts
// if (typeof window !== 'undefined') {
//   console.log('MindmapViewportStore created without persistence');
// }